/**
 * Custom hook for managing vacation requests
 * Handles data fetching, mutations, and state management for vacation requests
 */

import { useState, useCallback, useEffect } from 'react'
import {
  createVacationRequest,
  listMyVacationRequests,
  getVacationRequest,
  cancelVacationRequest
} from '@/lib/services/vacation.service'
import type {
  VacationRequest,
  VacationRequestStatus,
  CreateVacationRequestInput
} from '@/types/vacation'

interface UseVacationRequestsOptions {
  status?: VacationRequestStatus
  autoFetch?: boolean
}

interface UseVacationRequestsResult {
  requests: VacationRequest[]
  isLoading: boolean
  error: Error | null
  refetch: () => Promise<void>
}

/**
 * Hook to fetch and manage list of vacation requests
 */
export function useVacationRequests(
  options: UseVacationRequestsOptions = {}
): UseVacationRequestsResult {
  const { status, autoFetch = true } = options
  const [requests, setRequests] = useState<VacationRequest[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)

  const fetchRequests = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await listMyVacationRequests(status)
      setRequests(response.items)
    } catch (err) {
      setError(err as Error)
      console.error('Error fetching vacation requests:', err)
    } finally {
      setIsLoading(false)
    }
  }, [status])

  useEffect(() => {
    if (autoFetch) {
      fetchRequests()
    }
  }, [autoFetch, fetchRequests])

  return {
    requests,
    isLoading,
    error,
    refetch: fetchRequests
  }
}

interface UseCreateVacationRequestResult {
  createRequest: (input: CreateVacationRequestInput) => Promise<VacationRequest>
  isCreating: boolean
  error: Error | null
}

/**
 * Hook to create a new vacation request
 */
export function useCreateVacationRequest(): UseCreateVacationRequestResult {
  const [isCreating, setIsCreating] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)

  const createRequest = useCallback(
    async (input: CreateVacationRequestInput): Promise<VacationRequest> => {
      setIsCreating(true)
      setError(null)

      try {
        const request = await createVacationRequest(input)
        return request
      } catch (err) {
        setError(err as Error)
        throw err
      } finally {
        setIsCreating(false)
      }
    },
    []
  )

  return {
    createRequest,
    isCreating,
    error
  }
}

interface UseCancelVacationRequestResult {
  cancelRequest: (requestId: string) => Promise<void>
  isCanceling: boolean
  error: Error | null
}

/**
 * Hook to cancel a vacation request
 */
export function useCancelVacationRequest(): UseCancelVacationRequestResult {
  const [isCanceling, setIsCanceling] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)

  const cancelRequest = useCallback(async (requestId: string): Promise<void> => {
    setIsCanceling(true)
    setError(null)

    try {
      await cancelVacationRequest(requestId)
    } catch (err) {
      setError(err as Error)
      throw err
    } finally {
      setIsCanceling(false)
    }
  }, [])

  return {
    cancelRequest,
    isCanceling,
    error
  }
}

interface UseVacationRequestResult {
  request: VacationRequest | null
  isLoading: boolean
  error: Error | null
  refetch: () => Promise<void>
}

/**
 * Hook to fetch a single vacation request by ID
 */
export function useVacationRequest(requestId: string | null): UseVacationRequestResult {
  const [request, setRequest] = useState<VacationRequest | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)

  const fetchRequest = useCallback(async () => {
    if (!requestId) return

    setIsLoading(true)
    setError(null)

    try {
      const req = await getVacationRequest(requestId)
      setRequest(req)
    } catch (err) {
      setError(err as Error)
      console.error('Error fetching vacation request:', err)
    } finally {
      setIsLoading(false)
    }
  }, [requestId])

  useEffect(() => {
    if (requestId) {
      fetchRequest()
    }
  }, [requestId, fetchRequest])

  return {
    request,
    isLoading,
    error,
    refetch: fetchRequest
  }
}
